import { API } from "./api";

// One audio element for Dorothée and Yoda. No platform voice or competing queue.
let audio = null, generation = 0, controller = null, current = null, objectURL = null;
let activated = false, ready = false, sentenceCount = 0, nextCat = 8 + Math.floor(Math.random() * 3);
const listeners = new Set();
const state = { busy: false, paused: false, error: "" };
function emit(update) {
  Object.assign(state, update);
  listeners.forEach(fn => fn({ ...state }));
  window.dispatchEvent(new CustomEvent("dl-voice-state", { detail: { ...state } }));
}
function player() { if (!audio) { audio = new Audio(); audio.preload = "auto"; } return audio; }
function release() { if (objectURL) URL.revokeObjectURL(objectURL); objectURL = null; }
function chunks(text) {
  const result = [];
  let rest = String(text || "").trim();
  while (rest.length > 1600) {
    const head = rest.slice(0, 1600);
    let cut = Math.max(head.lastIndexOf(". "), head.lastIndexOf("! "), head.lastIndexOf("? "));
    if (cut < 500) cut = head.lastIndexOf(" ");
    if (cut < 1) cut = 1600; else cut += 1;
    result.push(rest.slice(0, cut)); rest = rest.slice(cut).trim();
  }
  if (rest) result.push(rest);
  return result;
}
export const dorotheeVoice = {
  subscribe(fn) { listeners.add(fn); fn({ ...state }); return () => listeners.delete(fn); },
  activate() {
    activated = true;
    // Unlock this same element during the user gesture (including Safari).
    const a = player();
    if (!state.busy) {
      a.src = "/audio/yoda-silence.wav";
      a.play().then(() => { if (!state.busy) { a.pause(); a.currentTime = 0; } }).catch(() => {});
    }
  },
  stop() {
    generation += 1; controller?.abort(); controller = null;
    if (audio) { audio.onended = null; audio.onerror = null; audio.pause(); audio.removeAttribute("src"); audio.load(); }
    release(); ready = false; const ended = current; current = null;
    emit({ busy: false, paused: false }); ended?.onend?.();
  },
  pause() { if (state.busy) { player().pause(); emit({ paused: true }); } },
  async resume() {
    if (!state.busy) return;
    if (!ready) { emit({ paused: false }); return; }
    try { await player().play(); emit({ paused: false, error: "" }); }
    catch { emit({ paused: true, error: "Appuie sur Reprendre pour autoriser le son." }); }
  },
  async speak(text, options = {}) {
    if (options.gesture) this.activate();
    if (!activated || document.hidden) return false;
    this.stop(); emit({ busy: true, paused: false, error: "" }); current = options;
    const token = generation;
    controller = new AbortController();
    const sentences = String(text || "").match(/[^.!?…]+(?:[.!?…]+|$)/g) || [];
    const parts = [];
    let group = "", count = sentenceCount;
    let limit = nextCat;
    for (const sentence of sentences) {
      if (group.length + sentence.length > 1600 && group) { parts.push(...chunks(group)); group = ""; }
      group += sentence; count += 1;
      if (count >= limit) { parts.push(...chunks(group)); group = ""; count = 0; limit = nextCat; }
    }
    if (group.trim()) parts.push(...chunks(group));
    const finish = () => { if (token === generation) { this.stop(); this.yodaIfDue(); } };
    const play = async (index) => {
      if (token !== generation) return;
      if (index >= parts.length) { finish(); return; }
      ready = false;
      try {
        const response = await fetch(`${API}/voice/speech`, {
          method: "POST", credentials: "include", signal: controller.signal,
          headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text: parts[index] }),
        });
        if (!response.ok) throw new Error("tts-unavailable");
        const blob = await response.blob();
        if (token !== generation) return;
        release(); objectURL = URL.createObjectURL(blob);
        const a = player(); a.src = objectURL; a.volume = 1; ready = true;
        a.onended = () => {
          if (token !== generation) return;
          sentenceCount += (parts[index].match(/[.!?…]+/g) || [1]).length;
          if (sentenceCount >= nextCat && localStorage.getItem("dl_muted") !== "1") {
            sentenceCount = 0;
            ready = true;
            a.src = Math.random() < 0.5 ? "/audio/yoda-purr.wav" : "/audio/yoda-meow.wav";
            a.volume = 0.23;
            a.onended = () => { if (token === generation) play(index + 1); };
            a.onerror = () => { if (token === generation) play(index + 1); };
            a.play().catch(() => { if (token === generation) play(index + 1); });
          } else play(index + 1);
        };
        a.onerror = () => { emit({ error: "La voix est indisponible. La réponse reste lisible." }); finish(); };
        if (state.paused) return;
        try { await a.play(); options.onstart?.(); }
        catch { if (token === generation) emit({ paused: true, error: "Appuie sur Reprendre pour autoriser le son." }); }
      } catch (error) {
        if (token !== generation || error.name === "AbortError") return;
        emit({ error: "La voix est indisponible. La réponse reste lisible." }); finish();
      }
    };
    await play(0);
    return true;
  },
  async yodaIfDue() {
    if (sentenceCount < nextCat || state.busy || !activated || document.hidden || localStorage.getItem("dl_muted") === "1") return;
    sentenceCount = 0;
    const a = player(); a.src = Math.random() < 0.5 ? "/audio/yoda-purr.wav" : "/audio/yoda-meow.wav";
    const token = generation; ready = true;
    a.volume = 0.23; emit({ busy: true, paused: false });
    a.onended = () => { if (token === generation) { ready = false; a.volume = 1; emit({ busy: false }); } };
    a.onerror = () => { if (token === generation) emit({ busy: false }); };
    window.dispatchEvent(new CustomEvent("dl-cat-say", { detail: { text: a.src.includes("purr") ? "prrr…" : "Miaou", ms: 1500 } }));
    try { await a.play(); } catch { if (token === generation) emit({ busy: false }); }
  },
};
